/* Full Range table (sections/etec-full-range.liquid).

   The section is served as plain, crawlable HTML. This script turns it into the interactive grid:

   - Categories come from the menu tree in [data-fr-config]. A category view shows its
     sub-categories as collapsible groups; a group loads its rows when opened, 100 at a time, from
     the JSON view templates/collection.fr-data.liquid on the category's tag URL
     (/collections/full-range/col-<handle>?view=fr-data). Products in the category but in none of
     its sub-categories go in an "Other" group. Pages are cached in memory.
   - Changing category uses history.pushState, so every view has its own linkable URL.
   - Sorting: name and price also sort on the server (sort_by), so "Load more" continues in order;
     the default (in stock first, then name), brand, SKU and stock sort the rows already loaded.
   - Search filters the loaded rows at once, then asks Shopify's search
     (templates/search.fr-data.liquid) for the rest of the range.
   - Brand, price and stock (all, in stock, out of stock) filter the loaded rows.
   - Add to cart (one row, or the ticked rows) goes through window.EtecCart from assets/etec.js,
     which refreshes the cart drawer and the cart count.
   - Enquire opens a <dialog> holding Shopify's contact form. It posts normally so Shopify's spam
     check keeps working; the page then reopens the dialog on its "sent" state.
   - "Download this view" builds a CSV of the rows on screen (UTF-8 with BOM, for Excel).
   - Out-of-stock products are Unlisted, so the collection feeds never return them. The nightly n8n
     workflow saves them as one JSON file (config.oosUrl); each group adds its share when it first
     loads. They sort last, read "Out of stock" and get a Contact us button that opens the enquiry
     form with "Notify me" ticked. The Stock filter shows only them, or hides them. */

(() => {
  if (customElements.get('etec-full-range')) return;

  const PAGE_SIZE = 100;
  const AUTO_PAGES = 5; // groups load further pages on scroll up to this many, then by button
  const LOAD_ALL_CAP = 3000; // most rows "load all and download" will fetch
  const STOCK_RANK = { in: 0, low: 1, preorder: 2, supplier: 3, out: 4 };
  const BUYABLE = new Set(['in', 'low', 'preorder']);
  const SERVER_SORTS = {
    name: ['title-ascending', 'title-descending'],
    price: ['price-ascending', 'price-descending'],
  };
  const ENQUIRY_KEY = 'etec-fr-enquiry';
  const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

  const h = (tag, attrs, ...children) => {
    const node = document.createElement(tag);
    if (attrs) {
      Object.entries(attrs).forEach(([key, value]) => {
        if (value === false || value == null) return;
        if (key === 'class') node.className = value;
        else if (key === 'text') node.textContent = value;
        else if (key === 'dataset') Object.assign(node.dataset, value);
        else if (value === true) node.setAttribute(key, '');
        else node.setAttribute(key, value);
      });
    }
    children.flat().forEach((child) => {
      if (child == null || child === false) return;
      node.append(child.nodeType ? child : document.createTextNode(String(child)));
    });
    return node;
  };

  const money = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });

  const plural = (count, word) => `${count.toLocaleString('en-AU')} ${word}${count === 1 ? '' : 's'}`;

  class EtecFullRange extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const configNode = this.querySelector('[data-fr-config]');
      if (!configNode) return;
      try {
        this.config = JSON.parse(configNode.textContent);
      } catch (error) {
        return;
      }
      this.ready = true;
      this.indexTree();

      this.cache = new Map();
      this.qty = new Map();
      this.selected = new Map();
      this.sort = { key: 'default', dir: 'asc' };
      this.filters = { query: '', brand: '', min: null, max: null, stock: '' };
      this.groupSeq = 0;

      this.dom = {
        table: this.querySelector('[data-fr-table]'),
        side: this.querySelector('[data-fr-side]'),
        toolbar: this.querySelector('[data-fr-toolbar]'),
        search: this.querySelector('[data-fr-search]'),
        brand: this.querySelector('[data-fr-brand]'),
        min: this.querySelector('[data-fr-min]'),
        max: this.querySelector('[data-fr-max]'),
        sortSelect: this.querySelector('[data-fr-sort]'),
        stock: this.querySelector('[data-fr-stock]'),
        addSelected: this.querySelector('[data-fr-add-selected]'),
        selectedCount: this.querySelector('[data-fr-selected-count]'),
        csv: this.querySelector('[data-fr-csv]'),
        prompt: this.querySelector('[data-fr-prompt]'),
        live: this.querySelector('[data-fr-live]'),
        title: this.querySelector('[data-fr-title]'),
        count: this.querySelector('[data-fr-count]'),
        crumbs: this.querySelector('[data-fr-crumbs]'),
        dialog: this.querySelector('[data-fr-dialog]'),
      };

      this.observer =
        'IntersectionObserver' in window
          ? new IntersectionObserver((entries) => this.onFootVisible(entries), { rootMargin: '400px 0px' })
          : null;

      this.setUpHead();
      this.setUpMobileNav();
      this.bind();
      this.setUpEnquiry();
      this.dom.toolbar.hidden = false;
      this.classList.add('is-ready');
      this.show(this.config.current || '', { push: false, initial: true });
    }

    /* ---------- Category tree ---------- */

    indexTree() {
      // Categories with no listed products (all out of stock or not for web) are left out.
      const prune = (list) =>
        list.filter((node) => {
          node.c = prune(node.c || []);
          return node.n !== 0 || node.h === this.config.current;
        });
      this.config.tree = prune(this.config.tree);
      this.nodes = new Map();
      const walk = (list, parent, depth) => {
        list.forEach((node) => {
          node.parent = parent;
          node.depth = depth;
          node.c = node.c || [];
        });
        // Shallowest wins: "Complete Systems" and its first child share a handle.
        list.forEach((node) => {
          if (!this.nodes.has(node.h)) this.nodes.set(node.h, node);
        });
        list.forEach((node) => walk(node.c, node, depth + 1));
      };
      walk(this.config.tree, null, 1);
    }

    descendants(node) {
      const out = new Set();
      const walk = (list) =>
        list.forEach((child) => {
          out.add(child.h);
          walk(child.c);
        });
      walk(node.c);
      return out;
    }

    // The deepest menu category a product belongs to, for the CSV.
    categoryOf(row, fallback) {
      let best = null;
      (row.c || []).forEach((handle) => {
        const node = this.nodes.get(handle);
        if (node && (!best || node.depth > best.depth)) best = node;
      });
      return best ? best.t : fallback || '';
    }

    trail(handle) {
      const out = [];
      let node = this.nodes.get(handle);
      while (node) {
        out.unshift(node);
        node = node.parent;
      }
      return out;
    }

    urlFor(handle) {
      const base = this.config.base;
      return handle ? `${base}/col-${encodeURIComponent(handle)}${this.config.view}` : `${base}${this.config.view}`;
    }

    /* ---------- Table head: sort buttons and select-all ---------- */

    setUpHead() {
      const head = this.dom.table.tHead;
      head.querySelectorAll('[data-fr-col]').forEach((th) => {
        const label = th.textContent.trim();
        th.textContent = '';
        th.append(h('button', { type: 'button', class: 'etec-fr__sort', dataset: { frSortKey: th.dataset.frCol } }, label));
        th.setAttribute('aria-sort', 'none');
      });
      const selCell = head.querySelector('.etec-fr__c-sel');
      selCell.textContent = '';
      this.dom.selectAll = h('input', {
        type: 'checkbox',
        class: 'etec-fr__check',
        'aria-label': 'Select all products shown that can be added to cart',
      });
      selCell.append(this.dom.selectAll);
      // Rows are rebuilt from JSON from here on.
      this.dom.table.querySelectorAll('tbody').forEach((body) => body.remove());
    }

    setUpMobileNav() {
      this.dom.mobileNav = h('div', { class: 'etec-fr__mobilenav' });
      const id = `FrCat-${this.dataset.sectionId || 'x'}`;
      this.dom.catSelect = h('select', { id });
      this.dom.chips = h('ul', { class: 'etec-fr__chips', role: 'list', 'aria-label': 'Sub-categories' });
      this.dom.mobileNav.append(
        h('div', { class: 'etec-fr__field' }, h('label', { for: id, text: 'Category' }), this.dom.catSelect),
        this.dom.chips
      );
      const option = (value, text) => h('option', { value }, text);
      this.dom.catSelect.append(option('', `All products (${this.config.total.toLocaleString('en-AU')})`));
      const addOptions = (list, depth) =>
        list.forEach((node) => {
          this.dom.catSelect.append(option(node.h, `${' '.repeat(depth)}${node.t} (${node.n})`));
          addOptions(node.c, depth + 1);
        });
      this.config.tree.forEach((node) => {
        this.dom.catSelect.append(option(node.h, `${node.t} (${node.n})`));
        addOptions(node.c, 1);
      });
      this.dom.side.prepend(this.dom.mobileNav);
    }

    /* ---------- Events ---------- */

    bind() {
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('input', (event) => this.onInput(event));

      this.dom.catSelect.addEventListener('change', () => this.show(this.dom.catSelect.value, { push: true }));

      window.addEventListener('popstate', () => {
        const match = window.location.pathname.match(/\/col-([^/?#]+)/);
        this.show(match ? decodeURIComponent(match[1]) : '', { push: false });
      });
    }

    onClick(event) {
      const target = event.target;

      const catLink = target.closest('a[data-fr-cat]');
      if (catLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
        event.preventDefault();
        this.show(catLink.dataset.frCat, { push: true });
        return;
      }

      const sortButton = target.closest('[data-fr-sort-key]');
      if (sortButton) {
        const key = sortButton.dataset.frSortKey;
        const dir = this.sort.key === key && this.sort.dir === 'asc' ? 'desc' : 'asc';
        this.setSort(key, dir);
        return;
      }

      const toggle = target.closest('[data-fr-toggle]');
      if (toggle) {
        const group = this.groupById(toggle.dataset.frToggle);
        if (group) this.toggleGroup(group);
        return;
      }

      const more = target.closest('[data-fr-more]');
      if (more) {
        const group = this.groupById(more.dataset.frMore);
        if (group) this.loadMore(group, { focus: true });
        return;
      }

      const action = target.closest('[data-fr-action]');
      if (action) {
        const row = this.rowById(action.dataset.frRow);
        if (!row) return;
        if (action.dataset.frAction === 'add') this.addToCart([row], action);
        if (action.dataset.frAction === 'enquire') this.openEnquiry(row, action);
        return;
      }

      if (target.closest('[data-fr-add-selected]')) {
        this.addToCart(Array.from(this.selected.values()), this.dom.addSelected);
        return;
      }

      if (target.closest('[data-fr-csv]')) {
        this.downloadView();
        return;
      }

      const promptAction = target.closest('[data-fr-prompt-action]');
      if (promptAction) {
        const choice = promptAction.dataset.frPromptAction;
        this.hidePrompt();
        if (choice === 'all') this.loadAllThenDownload();
        if (choice === 'shown') this.writeCsv();
        if (choice === 'cancel') this.dom.csv.focus();
      }
    }

    onChange(event) {
      const target = event.target;
      if (target === this.dom.selectAll) {
        this.visibleRows()
          .filter((row) => this.canBuy(row))
          .forEach((row) => (target.checked ? this.selected.set(row.v, row) : this.selected.delete(row.v)));
        this.renderGroups();
        this.updateSelected();
        return;
      }
      if (target.matches('[data-fr-select]')) {
        const row = this.rowById(target.dataset.frSelect);
        if (!row) return;
        if (target.checked) this.selected.set(row.v, row);
        else this.selected.delete(row.v);
        target.closest('tr').classList.toggle('is-selected', target.checked);
        this.updateSelected();
        return;
      }
      if (target === this.dom.brand) {
        this.filters.brand = target.value;
        this.renderGroups();
        return;
      }
      if (target === this.dom.stock) {
        this.filters.stock = target.value;
        this.renderGroups();
        return;
      }
      if (target === this.dom.sortSelect) {
        const [key, dir] = target.value.split(':');
        this.setSort(key, dir);
        return;
      }
      if (target.matches('[data-fr-qty]')) {
        this.qty.set(target.dataset.frQty, this.cleanQty(target));
      }
    }

    onInput(event) {
      const target = event.target;
      if (target === this.dom.search) {
        this.filters.query = target.value.trim();
        this.onSearch();
        return;
      }
      if (target === this.dom.min || target === this.dom.max) {
        const value = target.value === '' ? null : Math.round(parseFloat(target.value) * 100);
        this.filters[target === this.dom.min ? 'min' : 'max'] = Number.isFinite(value) ? value : null;
        clearTimeout(this.priceTimer);
        this.priceTimer = setTimeout(() => this.renderGroups(), 250);
        return;
      }
      if (target.matches('[data-fr-qty]')) {
        this.qty.set(target.dataset.frQty, target.value);
      }
    }

    /* ---------- Views and groups ---------- */

    show(handle, { push, initial } = {}) {
      const node = handle ? this.nodes.get(handle) : null;
      this.current = handle;
      this.currentNode = node;

      if (push) window.history.pushState({ frHandle: handle }, '', this.urlFor(handle));

      this.filters.query = '';
      this.filters.brand = '';
      this.dom.search.value = '';
      this.searchGroup = null;
      clearTimeout(this.searchTimer);

      this.groups = this.groupsFor(handle, node);
      const first = this.groups.find((group) => !group.nav);
      if (first) first.open = true;

      this.renderNav();
      this.renderHeading(node, handle);
      this.renderGroups();
      if (first) this.loadMore(first);
      if (!initial) {
        this.dom.title.setAttribute('tabindex', '-1');
        this.dom.title.focus({ preventScroll: false });
        this.announce(`Showing ${this.dom.title.textContent}`);
      }
    }

    groupsFor(handle, node) {
      if (!handle) return this.config.tree.map((top) => this.makeGroup(top, { nav: top.v }));
      if (!node) return [this.makeGroup({ h: handle, t: this.dom.title.textContent || handle, n: null, c: [] })];
      if (!node.c.length) return [this.makeGroup(node)];
      // A menu heading (Complete Systems): its children are the groups.
      if (node.v) return node.c.map((child) => this.makeGroup(child));
      // A real parent: one group per child, then its own products that are in none of them. A child
      // that links to the parent's own collection (Server Components) names that last group.
      const same = node.c.find((child) => child.h === node.h);
      const exclude = this.descendants(node);
      exclude.delete(node.h);
      const groups = node.c.filter((child) => child !== same).map((child) => this.makeGroup(child));
      groups.push(
        Object.assign(this.makeGroup(node), {
          kind: 'other',
          title: same ? same.t : `Other ${node.t}`,
          total: null,
          exclude,
        })
      );
      return groups;
    }

    makeGroup(node, extra) {
      this.groupSeq += 1;
      return Object.assign(
        {
          id: `g${this.groupSeq}`,
          kind: 'tag',
          handle: node.h,
          node,
          title: node.t,
          total: typeof node.n === 'number' ? node.n : null,
          rows: [],
          ids: new Set(),
          page: 0,
          pages: null,
          done: false,
          open: false,
          loading: false,
          error: '',
          nav: false,
        },
        extra || {}
      );
    }

    groupById(id) {
      if (this.searchGroup && this.searchGroup.id === id) return this.searchGroup;
      return (this.groups || []).find((group) => group.id === id);
    }

    activeGroups() {
      return this.searchGroup ? [this.searchGroup] : this.groups;
    }

    rowById(id) {
      for (const group of [this.searchGroup, ...(this.groups || [])]) {
        if (!group) continue;
        const row = group.rows.find((item) => String(item.v) === String(id));
        if (row) return row;
      }
      return this.selected.get(Number(id)) || null;
    }

    toggleGroup(group) {
      if (group.nav) {
        this.show(group.handle, { push: true });
        return;
      }
      group.open = !group.open;
      this.renderGroup(group);
      if (group.open && !group.page && !group.loading) this.loadMore(group);
      const button = this.dom.table.querySelector(`[data-fr-toggle="${group.id}"]`);
      if (button) button.focus();
    }

    serverSort() {
      const pair = SERVER_SORTS[this.sort.key];
      return pair ? pair[this.sort.dir === 'desc' ? 1 : 0] : 'title-ascending';
    }

    setSort(key, dir) {
      const before = this.serverSort();
      this.sort = { key, dir };
      this.dom.sortSelect.value = `${key}:${dir}`;
      if (this.dom.sortSelect.value !== `${key}:${dir}`) this.dom.sortSelect.value = 'default:asc';
      this.dom.table.tHead.querySelectorAll('[data-fr-col]').forEach((th) => {
        const sorted = th.dataset.frCol === key;
        th.setAttribute('aria-sort', sorted ? (dir === 'desc' ? 'descending' : 'ascending') : 'none');
      });
      // Groups that are only partly loaded must restart in the new server order.
      if (this.serverSort() !== before) {
        this.activeGroups().forEach((group) => {
          if (group.kind === 'search' || (group.done && group.page <= 1)) return;
          Object.assign(group, { rows: [], ids: new Set(), page: 0, pages: null, done: false, error: '', loading: false, oosAdded: false });
          group.seq = (group.seq || 0) + 1;
          if (group.open) this.loadMore(group);
        });
      }
      this.renderGroups();
      const label = { default: 'in stock first', name: 'name', brand: 'brand', sku: 'SKU', price: 'price', stock: 'stock' }[key];
      this.announce(`Sorted by ${label}${key === 'default' ? '' : dir === 'desc' ? ', descending' : ', ascending'}`);
    }

    /* ---------- Loading ---------- */

    tagUrl(handle, page) {
      const params = new URLSearchParams({ view: 'fr-data', sort_by: this.serverSort(), page: String(page) });
      return `${this.config.base}/col-${encodeURIComponent(handle)}?${params}`;
    }

    searchUrl(query, page) {
      const params = new URLSearchParams({
        q: query,
        type: 'product',
        'options[prefix]': 'last',
        view: 'fr-data',
        page: String(page),
      });
      return `${this.config.searchUrl}?${params}`;
    }

    fetchJson(url) {
      if (!this.cache.has(url)) {
        const get = () =>
          // No Accept: application/json header: Shopify answers that with a 404 on tag URLs.
          fetch(url, { credentials: 'same-origin' }).then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
          });
        // One quiet retry: a blip shouldn't show an error.
        const request = get()
          .catch(() => new Promise((resolve) => setTimeout(resolve, 800)).then(get))
          .catch((error) => {
            this.cache.delete(url);
            throw error;
          });
        this.cache.set(url, request);
      }
      return this.cache.get(url);
    }

    // The out-of-stock list, fetched once. A missing or broken file just means no out-of-stock rows.
    loadOos() {
      if (!this.oosRequest) {
        const url = this.config.oosUrl;
        this.oosRequest = !url
          ? Promise.resolve([])
          : fetch(url)
              .then((response) => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return response.json();
              })
              .then((data) =>
                (data.rows || []).map((row) =>
                  Object.assign(row, {
                    st: 'out',
                    oos: true,
                    p: this.config.oosPrices ? row.p : null,
                    pf: this.config.oosPrices ? money.format(row.p / 100) : '',
                  })
                )
              )
              .catch(() => []);
      }
      return this.oosRequest;
    }

    // The out-of-stock rows that belong in a group.
    async oosFor(group) {
      const rows = await this.loadOos();
      if (group.kind === 'search') return rows.filter((row) => this.matchesQuery(row) && this.inScope(row));
      return rows.filter((row) => {
        const cats = row.c || [];
        if (!cats.includes(group.handle)) return false;
        if (group.kind === 'other') return !cats.some((handle) => group.exclude.has(handle));
        return true;
      });
    }

    inScope(row) {
      if (!this.currentNode) return true;
      if (!this.scope) {
        this.scope = this.descendants(this.currentNode);
        this.scope.add(this.currentNode.h);
      }
      return (row.c || []).some((handle) => this.scope.has(handle));
    }

    async loadMore(group, { focus } = {}) {
      if (group.loading || group.done) return;
      group.seq = (group.seq || 0) + 1;
      const seq = group.seq;
      group.loading = true;
      group.error = '';
      this.renderGroup(group);
      let added = 0;
      let pagesRead = 0;
      try {
        do {
          const page = group.page + 1;
          const url = group.kind === 'search' ? this.searchUrl(group.query, page) : this.tagUrl(group.handle, page);
          const [data, oos] = await Promise.all([this.fetchJson(url), page === 1 ? this.oosFor(group) : null]);
          // Sorting restarted this group, or a newer search replaced it: drop this answer.
          if (group.seq !== seq) return;
          if (group.kind === 'search' && group !== this.searchGroup) return;
          group.page = page;
          group.pages = data.pages || 1;
          if (oos && !group.oosAdded) {
            group.oosAdded = true;
            oos.forEach((row) => {
              if (group.ids.has(row.id)) return;
              group.ids.add(row.id);
              group.rows.push(row);
              if (group.kind !== 'tag') added += 1;
            });
            group.oosCount = oos.length;
          }
          if (group.kind === 'tag' && typeof data.count === 'number') group.total = data.count + (group.oosCount || 0);
          if (page >= group.pages) group.done = true;
          (data.rows || []).forEach((row) => {
            if (group.kind === 'other' && (row.c || []).some((handle) => group.exclude.has(handle))) return;
            if (group.kind === 'search' && !this.inScope(row)) return;
            if (group.ids.has(row.id)) return;
            group.ids.add(row.id);
            group.rows.push(row);
            // Shopify's search is fuzzy; only rows that really match the words count towards a batch.
            if (group.kind !== 'search' || this.matchesQuery(row)) added += 1;
          });
          pagesRead += 1;
          // "Other" and search results are filtered here, so keep reading until there is
          // something to show (up to 10 pages per click).
        } while (!group.done && group.kind !== 'tag' && added < 25 && pagesRead < 10);
      } catch (error) {
        if (group.seq !== seq) return;
        group.error = "Couldn't load these products.";
      }
      group.loading = false;
      if (group.kind === 'other' && group.done && !group.rows.length) group.hidden = true;
      this.updateBrands();
      this.renderGroup(group);
      if (group.error) {
        this.announce(`${group.title}: ${group.error}`);
      } else {
        const of = group.total ? ` of ${group.total.toLocaleString('en-AU')}` : '';
        this.announce(`${group.title}: ${plural(group.rows.length, 'product')}${of} loaded`);
      }
      if (focus) {
        const next = this.dom.table.querySelector(`[data-fr-more="${group.id}"]`);
        const firstNew = this.dom.table.querySelectorAll(`tbody[data-group="${group.id}"] .etec-fr__row`)[
          group.rows.length - added
        ];
        const link = firstNew && firstNew.querySelector('a');
        (link || next || this.dom.table).focus();
      }
    }

    onFootVisible(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const group = this.groupById(entry.target.dataset.frFoot);
        if (group && group.open && !group.loading && !group.done && !group.error && group.page < AUTO_PAGES) {
          this.loadMore(group);
        }
      });
    }

    /* ---------- Search ---------- */

    onSearch() {
      clearTimeout(this.searchTimer);
      const query = this.filters.query;
      if (!query) {
        this.searchGroup = null;
        this.renderGroups();
        return;
      }
      // Instant: rows already loaded that match.
      const local = [];
      const seen = new Set();
      (this.groups || []).forEach((group) =>
        group.rows.forEach((row) => {
          if (seen.has(row.id) || !this.matchesQuery(row)) return;
          seen.add(row.id);
          local.push(row);
        })
      );
      const where = this.currentNode ? ` in ${this.currentNode.t}` : '';
      this.searchGroup = Object.assign(this.makeGroup({ h: '', t: `Results for “${query}”${where}`, n: null, c: [] }), {
        kind: 'search',
        query,
        rows: local,
        ids: seen,
        open: true,
        loading: query.length >= 2,
      });
      this.renderGroups();
      if (query.length < 2) return;
      this.searchTimer = setTimeout(() => {
        const group = this.searchGroup;
        if (!group || group.query !== query) return;
        group.loading = false;
        const term = query
          .split(/\s+/)
          .filter(Boolean)
          .join(' ');
        group.query = term;
        this.loadMore(group);
      }, 350);
    }

    matchesQuery(row) {
      const query = this.filters.query.toLowerCase();
      if (!query) return true;
      const text = `${row.t} ${row.s} ${row.b}`.toLowerCase();
      return query.split(/\s+/).every((word) => text.includes(word));
    }

    /* ---------- Filtering and sorting ---------- */

    // In stock and Low stock read as the units on hand ("12 in stock"); the colour still follows the state.
    stockLabel(row) {
      if ((row.st === 'in' || row.st === 'low') && typeof row.n === 'number' && row.n > 0) {
        return `${row.n.toLocaleString('en-AU')} in stock`;
      }
      return this.config.stock[row.st] || row.st;
    }

    canBuy(row) {
      return BUYABLE.has(row.st) && !row.m && row.v && row.q !== 0;
    }

    passes(row) {
      const f = this.filters;
      if (f.brand && row.b !== f.brand) return false;
      if (f.min != null && (row.p == null || row.p < f.min)) return false;
      if (f.max != null && (row.p == null || row.p > f.max)) return false;
      if (f.stock === 'in' && !BUYABLE.has(row.st)) return false;
      if (f.stock === 'out' && BUYABLE.has(row.st)) return false;
      if (f.query && !this.matchesQuery(row)) return false;
      return true;
    }

    compare(a, b) {
      const dir = this.sort.dir === 'desc' ? -1 : 1;
      const byName = collator.compare(a.t, b.t);
      switch (this.sort.key) {
        case 'name':
          return dir * byName;
        case 'brand':
          return dir * (collator.compare(a.b || '', b.b || '') || byName);
        case 'sku':
          if (!a.s !== !b.s) return a.s ? -1 : 1;
          return dir * (collator.compare(a.s || '', b.s || '') || byName);
        case 'price':
          if ((a.p == null) !== (b.p == null)) return a.p == null ? 1 : -1;
          return dir * ((a.p ?? 0) - (b.p ?? 0) || byName);
        case 'stock':
          return dir * ((STOCK_RANK[a.st] ?? 9) - (STOCK_RANK[b.st] ?? 9) || (b.n ?? 0) - (a.n ?? 0) || byName);
        default:
          return (STOCK_RANK[a.st] ?? 9) - (STOCK_RANK[b.st] ?? 9) || byName;
      }
    }

    shownRows(group) {
      return group.rows.filter((row) => this.passes(row)).sort((a, b) => this.compare(a, b));
    }

    visibleRows() {
      return this.activeGroups()
        .filter((group) => group.open && !group.hidden)
        .flatMap((group) => this.shownRows(group));
    }

    updateBrands() {
      const counts = new Map();
      [this.searchGroup, ...(this.groups || [])].forEach((group) => {
        if (!group) return;
        group.rows.forEach((row) => {
          if (row.b) counts.set(row.b, (counts.get(row.b) || 0) + 1);
        });
      });
      const select = this.dom.brand;
      const current = this.filters.brand;
      const names = Array.from(counts.keys()).sort(collator.compare);
      const signature = names.map((name) => `${name}:${counts.get(name)}`).join('|');
      if (signature === this.brandSignature) return;
      this.brandSignature = signature;
      select.replaceChildren(
        h('option', { value: '' }, 'All brands'),
        ...names.map((name) => h('option', { value: name }, `${name} (${counts.get(name)})`))
      );
      if (current && !counts.has(current)) select.append(h('option', { value: current }, current));
      select.value = current;
    }

    /* ---------- Rendering ---------- */

    renderNav() {
      // Desktop list: top level always; the current branch opened.
      const trail = this.trail(this.current);
      const openHandles = new Set(trail.map((node) => node.h));
      const link = (node, count) => {
        const isCurrent = node.h === this.current && trail[trail.length - 1] === node;
        return h(
          'a',
          {
            class: 'etec-fr__cat',
            href: this.urlFor(node.h),
            dataset: { frCat: node.h },
            'aria-current': isCurrent ? 'page' : null,
          },
          h('span', null, node.t),
          count == null ? null : h('span', { class: 'etec-fr__cat-count' }, count.toLocaleString('en-AU'))
        );
      };
      const list = (nodes, sub) =>
        h(
          'ul',
          { class: `etec-fr__tree${sub ? ' etec-fr__tree--sub' : ''}`, role: 'list' },
          nodes.map((node) =>
            h('li', null, link(node, node.n), openHandles.has(node.h) && node.c.length ? list(node.c, true) : null)
          )
        );
      const all = h(
        'a',
        {
          class: 'etec-fr__cat',
          href: this.urlFor(''),
          dataset: { frCat: '' },
          'aria-current': this.current ? null : 'page',
        },
        h('span', null, 'All products'),
        h('span', { class: 'etec-fr__cat-count' }, this.config.total.toLocaleString('en-AU'))
      );
      const nav = this.dom.side.querySelector('nav');
      const tree = list(this.config.tree, false);
      tree.prepend(h('li', null, all));
      nav.querySelector('.etec-fr__tree').replaceWith(tree);

      // Phones: select menu plus chips for the next level down (or siblings at a leaf).
      this.dom.catSelect.value = this.current || '';
      const node = this.currentNode;
      let chipsFor = this.config.tree;
      let parentChip = null;
      if (node && node.c.length) {
        chipsFor = node.c;
        parentChip = node;
      } else if (node && node.parent) {
        chipsFor = node.parent.c;
        parentChip = node.parent;
      }
      this.dom.chips.replaceChildren(
        ...(parentChip
          ? [h('li', null, this.chip(parentChip, `All ${parentChip.t}`, parentChip.h === this.current))]
          : [h('li', null, this.chip({ h: '', t: 'All', n: this.config.total }, 'All', !this.current))]),
        ...chipsFor.map((child) => h('li', null, this.chip(child, child.t, child.h === this.current)))
      );
    }

    chip(node, label, current) {
      return h(
        'a',
        {
          class: 'etec-fr__chip',
          href: this.urlFor(node.h),
          dataset: { frCat: node.h },
          'aria-current': current ? 'page' : null,
        },
        label,
        node.n != null ? h('span', { class: 'etec-fr__cat-count' }, node.n) : null
      );
    }

    renderHeading(node, handle) {
      const shop = this.config.shop;
      const title = node ? node.t : handle ? this.dom.title.textContent : this.config.heading;
      this.dom.title.textContent = title;
      const count = node ? node.n : handle ? null : this.config.total;
      if (count != null) this.dom.count.textContent = plural(count, 'product');
      document.title = handle ? `${title} | ${this.config.heading} – ${shop}` : `${this.config.heading} – ${shop}`;

      const crumbs = this.dom.crumbs;
      const home = crumbs.querySelector('a');
      const sep = () => h('span', { 'aria-hidden': 'true' }, '/');
      const parts = [home.cloneNode(true), sep()];
      if (!handle) {
        parts.push(h('span', { class: 'etec-crumbs__current', 'aria-current': 'page' }, this.config.heading));
      } else {
        parts.push(h('a', { href: this.urlFor(''), dataset: { frCat: '' } }, this.config.heading));
        const trail = this.trail(handle);
        trail.slice(0, -1).forEach((step) => {
          parts.push(sep(), h('a', { href: this.urlFor(step.h), dataset: { frCat: step.h } }, step.t));
        });
        parts.push(sep(), h('span', { class: 'etec-crumbs__current', 'aria-current': 'page' }, title));
      }
      crumbs.replaceChildren(...parts);
      if (this.dom.table.caption) this.dom.table.caption.textContent = `Products in ${title}`;
      this.scope = null;
    }

    renderGroups() {
      const table = this.dom.table;
      table.querySelectorAll('tbody').forEach((body) => body.remove());
      if (this.observer) this.observer.disconnect();
      const groups = this.activeGroups();
      groups.forEach((group) => table.append(this.buildGroup(group)));
      this.updateSelected();
    }

    renderGroup(group) {
      const old = this.dom.table.querySelector(`tbody[data-group="${group.id}"]`);
      if (!old) return;
      const active = document.activeElement;
      const focusKey = old.contains(active) ? active.dataset.focusKey : null;
      const fresh = this.buildGroup(group);
      old.replaceWith(fresh);
      if (focusKey) {
        const again = fresh.querySelector(`[data-focus-key="${focusKey}"]`);
        if (again) again.focus();
      }
      this.updateSelected();
    }

    buildGroup(group) {
      const body = h('tbody', { class: 'etec-fr__group', dataset: { group: group.id } });
      if (group.hidden) {
        body.hidden = true;
        return body;
      }
      const single = this.activeGroups().length === 1;
      const rows = group.open ? this.shownRows(group) : [];

      if (!single || group.kind === 'search') {
        const countText = (() => {
          if (group.kind === 'search') {
            return group.loading ? 'Searching…' : plural(rows.length, 'result');
          }
          if (group.total != null) return plural(group.total, 'product');
          return group.rows.length ? plural(group.rows.length, 'product') : '';
        })();
        const toggleAttrs = {
          class: 'etec-fr__group-toggle',
          dataset: { frToggle: group.id, focusKey: `toggle-${group.id}` },
        };
        const toggle = group.nav
          ? h(
              'a',
              Object.assign(toggleAttrs, { href: this.urlFor(group.handle), dataset: { frCat: group.handle, focusKey: `toggle-${group.id}` } }),
              h('span', { class: 'etec-fr__chev', 'aria-hidden': 'true' }),
              h('span', { class: 'etec-fr__group-title' }, group.title),
              h('span', { class: 'etec-fr__group-count' }, countText)
            )
          : h(
              'button',
              Object.assign(toggleAttrs, { type: 'button', 'aria-expanded': String(group.open) }),
              h('span', { class: 'etec-fr__chev', 'aria-hidden': 'true' }),
              h('span', { class: 'etec-fr__group-title' }, group.title),
              h('span', { class: 'etec-fr__group-count' }, countText)
            );
        const node = group.node;
        const openLink =
          !group.nav && group.kind === 'tag' && node && node.h && node.h !== this.current && this.nodes.has(node.h)
            ? h('a', { class: 'etec-fr__group-link', href: this.urlFor(node.h), dataset: { frCat: node.h } }, 'View category')
            : null;
        body.append(
          h(
            'tr',
            { class: 'etec-fr__group-head' },
            h('th', { colspan: '9', scope: 'rowgroup' }, h('div', { class: 'etec-fr__group-bar' }, toggle, openLink))
          )
        );
      }

      if (!group.open) return body;

      rows.forEach((row) => body.append(this.buildRow(row)));

      const foot = h('td', { colspan: '9' });
      const footRow = h('tr', { class: 'etec-fr__foot', dataset: { frFoot: group.id } }, foot);
      if (group.loading) {
        foot.append(h('span', { class: 'etec-fr__loading' }, group.kind === 'search' ? 'Searching the full range…' : 'Loading products…'));
      } else if (group.error) {
        foot.append(
          `${group.error} `,
          h(
            'button',
            { type: 'button', class: 'etec-fr__btn etec-fr__btn--ghost', dataset: { frMore: group.id, focusKey: `more-${group.id}` } },
            'Try again'
          )
        );
      } else if (!group.done && group.page > 0) {
        const of = group.total ? ` (${group.rows.length.toLocaleString('en-AU')} of ${group.total.toLocaleString('en-AU')})` : '';
        foot.append(
          h(
            'button',
            { type: 'button', class: 'etec-fr__btn etec-fr__btn--ghost', dataset: { frMore: group.id, focusKey: `more-${group.id}` } },
            `Load ${PAGE_SIZE} more${of}`
          )
        );
        if (this.observer && group.page < AUTO_PAGES) this.observer.observe(footRow);
      } else if (!rows.length) {
        foot.append(
          group.rows.length
            ? 'No products here match the filters.'
            : group.kind === 'search'
              ? 'No products match your search.'
              : 'No products in this category yet.'
        );
      } else if (group.rows.length > rows.length) {
        foot.append(`${plural(rows.length, 'product')} match the filters (of ${group.rows.length.toLocaleString('en-AU')} loaded).`);
      } else {
        foot.append(`All ${plural(rows.length, 'product')} shown.`);
      }
      body.append(footRow);
      return body;
    }

    buildRow(row) {
      const buyable = this.canBuy(row);
      const label = this.stockLabel(row);
      const selected = this.selected.has(row.v);
      const qtyValue = this.qty.get(String(row.v)) || '1';

      const thumb = row.i
        ? h('img', { class: 'etec-fr__thumb', src: row.i, alt: '', width: '48', height: '48', loading: 'lazy', decoding: 'async' })
        : h('span', { class: 'etec-fr__thumb etec-noimg', 'aria-hidden': 'true' });

      let qtyCell = null;
      let action;
      if (buyable) {
        const qtyId = `FrQ-${row.v}`;
        qtyCell = [
          h('label', { class: 'visually-hidden', for: qtyId }, `Quantity for ${row.t}`),
          h('input', {
            id: qtyId,
            class: 'etec-fr__qty',
            type: 'number',
            min: '1',
            max: row.q != null ? String(row.q) : '999',
            step: '1',
            inputmode: 'numeric',
            value: qtyValue,
            dataset: { frQty: String(row.v), focusKey: `qty-${row.v}` },
          }),
        ];
        action = h(
          'button',
          {
            type: 'button',
            class: 'etec-fr__btn etec-fr__btn--accent',
            dataset: { frAction: 'add', frRow: String(row.v), focusKey: `add-${row.v}` },
            'aria-label': `Add ${row.t} to cart`,
          },
          'Add to cart'
        );
      } else if (row.m && BUYABLE.has(row.st)) {
        action = h('a', { class: 'etec-fr__btn etec-fr__btn--ghost', href: row.u }, 'Choose options');
      } else {
        action = h(
          'button',
          {
            type: 'button',
            class: 'etec-fr__btn etec-fr__btn--ghost',
            dataset: { frAction: 'enquire', frRow: String(row.v), focusKey: `enq-${row.v}` },
            'aria-label': `${row.oos ? this.config.contactLabel : 'Enquire'} about ${row.t}`,
            'aria-haspopup': 'dialog',
          },
          row.oos ? this.config.contactLabel : 'Enquire'
        );
      }

      return h(
        'tr',
        { class: `etec-fr__row${selected ? ' is-selected' : ''}`, dataset: { state: row.st } },
        h(
          'td',
          { class: 'etec-fr__c-sel' },
          h('input', {
            type: 'checkbox',
            class: 'etec-fr__check',
            checked: selected,
            disabled: !buyable,
            'aria-label': buyable ? `Select ${row.t}` : `${row.t} can't be added to cart`,
            dataset: { frSelect: String(row.v), focusKey: `sel-${row.v}` },
          })
        ),
        h('td', { class: 'etec-fr__c-img' }, thumb),
        h('th', { scope: 'row', class: 'etec-fr__c-name' }, h('a', { class: 'etec-fr__name', href: row.u, title: row.t }, row.t)),
        h('td', { class: 'etec-fr__c-brand' }, row.b || ''),
        h('td', { class: 'etec-fr__c-sku' }, row.s ? h('code', null, row.s) : ''),
        h('td', { class: 'etec-fr__c-price' }, row.pf),
        h(
          'td',
          { class: 'etec-fr__c-stock' },
          h('span', { class: `etec-fr__stock etec-fr__stock--${row.st}` }, h('span', { class: 'etec-fr__dot', 'aria-hidden': 'true' }), label)
        ),
        h('td', { class: 'etec-fr__c-qty' }, qtyCell),
        h('td', { class: 'etec-fr__c-act' }, action)
      );
    }

    updateSelected() {
      const count = this.selected.size;
      this.dom.addSelected.disabled = count === 0;
      this.dom.selectedCount.textContent = count ? `(${count})` : '';
      const buyable = this.visibleRows().filter((row) => this.canBuy(row));
      const all = buyable.length > 0 && buyable.every((row) => this.selected.has(row.v));
      this.dom.selectAll.checked = all;
      this.dom.selectAll.indeterminate = !all && buyable.some((row) => this.selected.has(row.v));
      this.dom.selectAll.disabled = buyable.length === 0;
    }

    announce(text) {
      const live = this.dom.live;
      live.textContent = '';
      clearTimeout(this.liveTimer);
      this.liveTimer = setTimeout(() => (live.textContent = text), 60);
    }

    /* ---------- Cart ---------- */

    cleanQty(input) {
      let value = parseInt(input.value, 10);
      if (!Number.isFinite(value) || value < 1) value = 1;
      const max = parseInt(input.max, 10);
      if (Number.isFinite(max) && value > max) value = max;
      input.value = String(value);
      return String(value);
    }

    quantityFor(row) {
      const input = this.dom.table.querySelector(`[data-fr-qty="${row.v}"]`);
      if (input) return parseInt(this.cleanQty(input), 10);
      let value = parseInt(this.qty.get(String(row.v)) || '1', 10);
      if (!Number.isFinite(value) || value < 1) value = 1;
      if (row.q != null && value > row.q) value = row.q;
      return value;
    }

    async addToCart(rows, trigger) {
      const items = rows.filter((row) => this.canBuy(row)).map((row) => ({ id: row.v, quantity: this.quantityFor(row) }));
      if (!items.length || !trigger || trigger.classList.contains('is-busy')) return;
      const labelNode = trigger.querySelector('[data-fr-label]') || trigger;
      const label = labelNode.textContent;
      const finish = (state, text) => {
        trigger.classList.remove('is-busy');
        trigger.classList.add(state);
        labelNode.textContent = text;
        setTimeout(() => {
          trigger.classList.remove(state);
          labelNode.textContent = label;
          trigger.disabled = trigger === this.dom.addSelected && this.selected.size === 0;
        }, 2200);
      };
      trigger.classList.add('is-busy');
      trigger.disabled = true;
      labelNode.textContent = 'Adding…';

      const cart = window.EtecCart;
      try {
        let data;
        const sections = cart ? cart.sections() : null;
        if (cart) {
          data = await cart.request('cart/add.js', Object.assign({ items }, sections || {}));
        } else {
          const response = await fetch(`${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          data = await response.json();
          if (!response.ok || data.status) throw new Error(data.description || data.message || '');
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'etec-full-range', cartData: data });
        }
        const units = items.reduce((sum, item) => sum + item.quantity, 0);
        const what = rows.length === 1 ? rows[0].t : plural(items.length, 'product');
        if (trigger === this.dom.addSelected) {
          rows.forEach((row) => this.selected.delete(row.v));
          this.renderGroups();
        }
        finish('is-done', 'Added ✓');
        this.announce(`Added ${units} × ${what} to your cart.`);
        // No cart drawer to refresh (or it failed): show the cart page instead.
        const shown = Boolean(sections) && cart.render(data, trigger);
        if (!shown) window.location.href = cart ? cart.url() : '/cart';
      } catch (error) {
        finish('is-error', 'Try again');
        this.announce(error && error.message ? error.message : "Couldn't add to cart. Please try again.");
      }
    }

    /* ---------- Enquiry dialog ---------- */

    setUpEnquiry() {
      const dialog = this.dom.dialog;
      if (!dialog) return;
      const form = dialog.querySelector('form');
      this.dom.form = form;

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog || event.target.closest('[data-fr-close]')) this.closeEnquiry();
      });
      dialog.addEventListener('close', () => {
        if (this.enquiryTrigger && document.contains(this.enquiryTrigger)) this.enquiryTrigger.focus();
        this.enquiryTrigger = null;
      });
      if (form) {
        form.addEventListener('submit', (event) => this.onEnquirySubmit(event));
        form.addEventListener('input', (event) => {
          if (event.target.getAttribute('aria-invalid') === 'true') this.validateField(event.target);
        });
      }

      // Back from Shopify after sending (or with an error): reopen the dialog on that state.
      const success = dialog.querySelector('[data-fr-success]');
      const serverError = dialog.querySelector('[data-fr-server-error]');
      if (success || serverError) {
        let saved = null;
        try {
          saved = JSON.parse(window.sessionStorage.getItem(ENQUIRY_KEY) || 'null');
        } catch (error) {
          saved = null;
        }
        if (success) {
          const text = success.querySelector('[data-fr-success-text]');
          if (saved && saved.t && text) text.textContent = `We've got your enquiry about ${saved.t}. ${text.textContent}`;
          try {
            window.sessionStorage.removeItem(ENQUIRY_KEY);
          } catch (error) {
            /* storage unavailable */
          }
        } else if (saved) {
          this.fillEnquiry(saved);
        }
        this.showDialog();
        (success || dialog.querySelector('[aria-invalid="true"], input:not([readonly]):not([type="hidden"])')).focus();
        if (window.location.search.includes('contact_posted')) {
          const url = new URL(window.location.href);
          url.searchParams.delete('contact_posted');
          window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        }
      }
    }

    showDialog() {
      const dialog = this.dom.dialog;
      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    }

    closeEnquiry() {
      const dialog = this.dom.dialog;
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }

    fillEnquiry(data) {
      const form = this.dom.form;
      if (!form) return;
      const set = (key, value) => {
        const field = form.querySelector(`[data-fr-f="${key}"]`);
        if (field) field.value = value == null ? '' : value;
      };
      set('product', data.t);
      set('sku', data.s || 'n/a');
      set('url', data.u ? new URL(data.u, window.location.origin).href : '');
      set('qty', data.qty || 1);
    }

    openEnquiry(row, trigger) {
      const dialog = this.dom.dialog;
      if (!dialog) return;
      const form = this.dom.form;
      if (!form || !form.querySelector('[data-fr-f="product"]')) {
        // The dialog is still showing a "sent" message from the last enquiry: reload it as a form.
        window.location.href = this.urlFor(this.current);
        return;
      }
      this.enquiryTrigger = trigger;
      this.fillEnquiry({ t: row.t, s: row.s, u: row.u, qty: this.quantityFor(row) });
      const notify = form.querySelector('[data-fr-f="notify"]');
      if (notify) notify.checked = row.st === 'out';
      form.querySelectorAll('[aria-invalid]').forEach((field) => this.clearError(field));
      this.showDialog();
      const first = form.querySelector('#FrEnqName');
      (first && !first.value ? first : form.querySelector('#FrEnqQty')).focus();
    }

    validateField(field) {
      const value = field.value.trim();
      let message = '';
      switch (field.id) {
        case 'FrEnqName':
          if (!value) message = 'Enter your name.';
          break;
        case 'FrEnqEmail':
          if (!value) message = 'Enter your email address.';
          else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) message = 'Enter an email address like name@example.com.';
          break;
        case 'FrEnqPhone':
          if (value && !/^\+?[\d\s()-]{6,20}$/.test(value)) message = 'Enter a phone number using digits, spaces and + only.';
          break;
        case 'FrEnqQty': {
          const number = Number(value);
          if (!Number.isInteger(number) || number < 1 || number > 9999) message = 'Enter a quantity from 1 to 9999.';
          break;
        }
        default:
          break;
      }
      const error = this.querySelector(`#${field.id}-error`);
      if (message) {
        field.setAttribute('aria-invalid', 'true');
        if (error) {
          error.textContent = message;
          error.hidden = false;
        }
      } else {
        this.clearError(field);
      }
      return !message;
    }

    clearError(field) {
      field.removeAttribute('aria-invalid');
      const error = this.querySelector(`#${field.id}-error`);
      if (error) {
        error.textContent = '';
        error.hidden = true;
      }
    }

    onEnquirySubmit(event) {
      const form = this.dom.form;
      const fields = ['FrEnqQty', 'FrEnqName', 'FrEnqEmail', 'FrEnqPhone'].map((id) => form.querySelector(`#${id}`)).filter(Boolean);
      const bad = fields.filter((field) => !this.validateField(field));
      if (bad.length) {
        event.preventDefault();
        bad[0].focus();
        this.announce(`Please fix ${plural(bad.length, 'field')} before sending.`);
        return;
      }
      const get = (key) => (form.querySelector(`[data-fr-f="${key}"]`) || {}).value || '';
      try {
        window.sessionStorage.setItem(
          ENQUIRY_KEY,
          JSON.stringify({ t: get('product'), s: get('sku'), u: get('url'), qty: get('qty'), at: Date.now() })
        );
      } catch (error) {
        /* storage unavailable: the sent message just won't name the product */
      }
      const submit = form.querySelector('[type="submit"]');
      if (submit) {
        submit.classList.add('is-busy');
        submit.setAttribute('aria-disabled', 'true');
        submit.textContent = 'Sending…';
      }
    }

    /* ---------- CSV ---------- */

    downloadView() {
      const partial = this.activeGroups().filter((group) => group.open && !group.hidden && !group.done);
      if (!partial.length) {
        this.writeCsv();
        return;
      }
      const loaded = this.visibleRows().length;
      const total = partial.reduce((sum, group) => sum + (group.total || 0), 0);
      const prompt = this.dom.prompt;
      prompt.replaceChildren(
        h(
          'span',
          null,
          `${plural(loaded, 'row')} are loaded. ${partial.length === 1 ? 'One open group has' : `${partial.length} open groups have`} more to load${
            total ? ` (about ${total.toLocaleString('en-AU')} in total)` : ''
          }.`
        ),
        h('button', { type: 'button', class: 'etec-fr__btn etec-fr__btn--accent', dataset: { frPromptAction: 'all' } }, 'Load all and download'),
        h('button', { type: 'button', class: 'etec-fr__btn etec-fr__btn--ghost', dataset: { frPromptAction: 'shown' } }, 'Download loaded rows'),
        h('button', { type: 'button', class: 'etec-fr__btn etec-fr__btn--ghost', dataset: { frPromptAction: 'cancel' } }, 'Cancel')
      );
      prompt.hidden = false;
      prompt.querySelector('button').focus();
    }

    hidePrompt() {
      this.dom.prompt.hidden = true;
      this.dom.prompt.replaceChildren();
    }

    async loadAllThenDownload() {
      this.dom.csv.disabled = true;
      this.announce('Loading all rows for the download…');
      const groups = this.activeGroups().filter((group) => group.open && !group.hidden);
      const count = () => groups.reduce((sum, group) => sum + group.rows.length, 0);
      for (const group of groups) {
        while (!group.done && !group.error && count() < LOAD_ALL_CAP) {
          // eslint-disable-next-line no-await-in-loop
          await this.loadMore(group);
          if (group.loading) break;
        }
      }
      this.dom.csv.disabled = false;
      this.writeCsv(count() >= LOAD_ALL_CAP);
      this.dom.csv.focus();
    }

    // The CSV text for the rows on screen: open groups, after filters, in the current sort.
    buildCsv() {
      const cell = (value) => {
        let text = value == null ? '' : String(value);
        // Stop spreadsheet apps reading a cell as a formula.
        if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
        if (/[",\r\n]/.test(text) || /^\s|\s$/.test(text)) text = `"${text.replace(/"/g, '""')}"`;
        return text;
      };
      const origin = window.location.origin;
      const lines = [['Name', 'Brand', 'SKU', 'Category', this.config.priceLabel, 'Stock status', 'Product URL'].map(cell).join(',')];
      let count = 0;
      this.activeGroups()
        .filter((group) => group.open && !group.hidden)
        .forEach((group) => {
          const fallback = group.kind === 'search' ? '' : group.node && group.node.t;
          this.shownRows(group).forEach((row) => {
            count += 1;
            lines.push(
              [
                row.t,
                row.b,
                row.s,
                this.categoryOf(row, fallback),
                row.p == null ? '' : (row.p / 100).toFixed(2),
                this.stockLabel(row),
                new URL(row.u, origin).href,
              ]
                .map(cell)
                .join(',')
            );
          });
        });
      return { text: `\uFEFF${lines.join('\r\n')}\r\n`, count };
    }

    writeCsv(capped) {
      const { text, count } = this.buildCsv();
      if (!count) {
        this.announce('There are no rows on screen to download.');
        return;
      }
      const date = new Date();
      const stamp = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const name = `etecplus-full-range-${this.current || 'all'}${this.filters.query ? '-search' : ''}-${stamp}.csv`;
      const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = h('a', { href: url, download: name, hidden: true });
      document.body.append(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      this.announce(`Downloaded ${plural(count, 'row')}${capped ? ` (stopped at ${LOAD_ALL_CAP.toLocaleString('en-AU')})` : ''}.`);
    }
  }

  customElements.define('etec-full-range', EtecFullRange);
})();
